import BaseTooltip from 'components/Tooltips/BaseTooltip';
import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { useId, type ReactNode } from 'react';
import { Form } from 'react-bootstrap';
import { FaInfoCircle } from 'react-icons/fa';
import './floodSettingsHelp.css';

type CommonProps = {
  label: string;
  labelClassName?: string;
  /** Hover help: what the field does, in one or two sentences. Renders an
   *  info mark after the label; the text is also the mark's title. */
  help?: ReactNode;
};

/** The info mark. Keyboard-reachable so the tooltip opens on focus as well.
 *  Exported for fields that lay out their own controls (DurationField). */
export function HelpHint({ text }: { text: ReactNode }) {
  return (
    <BaseTooltip text={text} placement="top" className="responder-tooltip">
      <span
        className="ms-2 text-info align-middle flood-settings-help-trigger"
        role="img"
        aria-label="help"
        tabIndex={0}
      >
        <FaInfoCircle size={12} />
      </span>
    </BaseTooltip>
  );
}

// Default variant — renders a Form.Control input (text, number, time, etc.)
// Used in: FloodSimSettings (numeric/text fields), FloodDeviceSettings (Hour Start, Hour End, Destination Port)
type ControlVariant = CommonProps &
  Pick<
    React.ComponentProps<typeof Form.Control>,
    'value' | 'onChange' | 'type' | 'min' | 'max' | 'step' | 'placeholder' | 'disabled'
  > & {
    variant?: 'control';
    className?: string;
  };

// Renders a Form.Check switch toggle
// Used in: FloodSimSettings (Enabled, Loop Enabled), FloodDeviceSettings (Reboot)
type ToggleVariant = CommonProps &
  Pick<React.ComponentProps<typeof Form.Check>, 'id' | 'checked' | 'onChange' | 'disabled'> & {
    variant: 'toggle';
  };

// Renders a BaseSelect (react-select) picker; pass flat or grouped options
// Used in: FloodSimSettings (Donor Source, Scenario, waterway pickers)
export type FloodSelectOption = {
  value: string;
  label: string;
  isDisabled?: boolean;
};

export type FloodSelectGroup = {
  label: string;
  options: FloodSelectOption[];
};

type SelectVariant = CommonProps & {
  variant: 'select';
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  options: Array<FloodSelectOption | FloodSelectGroup>;
  placeholder?: string;
  isSearchable?: boolean;
};

export type FloodSettingsFieldProps = ControlVariant | ToggleVariant | SelectVariant;

export default function FloodSettingsField(props: FloodSettingsFieldProps) {
  const { label, labelClassName, help } = props;
  const labelClass = `mb-0${labelClassName ? ` ${labelClassName}` : ''}`;
  // Form.Group's controlId wires the label to the control it renders; the
  // toggle keeps its caller-supplied id and the select is told the same id.
  const generatedId = useId();
  const controlId = props.variant === 'toggle' ? props.id : generatedId;

  return (
    <Form.Group controlId={controlId} className="row align-items-center g-2 flood-settings-field">
      <div className="col-lg-5">
        <Form.Label className={labelClass}>
          {label}
          {help ? <HelpHint text={help} /> : null}
        </Form.Label>
      </div>
      {props.variant === 'toggle' ? (
        <div className="col-lg-7 d-flex justify-content-lg-end">
          <Form.Check
            type="switch"
            id={props.id}
            checked={props.checked}
            onChange={props.onChange}
            disabled={props.disabled}
          />
        </div>
      ) : props.variant === 'select' ? (
        <div className="col-lg-7 min-w-0">
          <BaseSelect<FloodSelectOption>
            inputId={controlId}
            wrapperClassName="min-w-0"
            isSearchable={props.isSearchable ?? true}
            maxMenuHeight={240}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 'var(--z-popover)' }),
            }}
            classNames={{
              menu: () => 'w-100 overflow-hidden',
              menuList: () => 'w-100 overflow-x-hidden',
              option: () => 'text-wrap text-break lh-sm',
              singleValue: () => 'text-truncate',
            }}
            selectProps={{
              options: props.options,
              value:
                props.options
                  .flatMap((option) => ('options' in option ? option.options : option))
                  .find((option) => option.value === props.value) ?? null,
              placeholder: props.placeholder,
              isDisabled: props.disabled,
              isOptionDisabled: (option) => !!option.isDisabled,
              menuPortalTarget: document.body,
              menuPosition: 'fixed',
              onChange: (option) => props.onChange(option?.value ?? ''),
            }}
          />
        </div>
      ) : (
        <div className="col-lg-7">
          <Form.Control
            className={props.className ?? 'flood-settings-control'}
            value={props.value}
            onChange={props.onChange}
            type={props.type}
            min={props.min}
            max={props.max}
            step={props.step}
            placeholder={props.placeholder}
            disabled={props.disabled}
          />
        </div>
      )}
    </Form.Group>
  );
}
